import { Alert, Button, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowSummary, type WorkflowVersionSummary, workflowsApi } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { message } from '@/lib/message';

import { extractApiError } from './statusUtils';

const { Text, Paragraph } = Typography;

type Props = {
  /** Null = closed. Non-null = open for that workflow. */
  workflow: WorkflowSummary | null;
  onClose: () => void;
};

/**
 * Read-only popup that surfaces the approver's rejection comment for the
 * most-recently rejected version of a workflow. Opened by clicking the
 * Rejected tag on a workflow card.
 *
 * Design: the comment is the star — big quoted block front and centre;
 * the workflow name is the heading; who/when live in one muted footer
 * line. No version chip (per PM feedback: "we don't need to advertise
 * which version was rejected — the originator only sees this modal for
 * the workflow they're about to fix").
 */
export function RejectionDetailsModal({ workflow, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const [version, setVersion] = useState<WorkflowVersionSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workflow) {
      setVersion(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await workflowsApi.listVersions(workflow.id);
        if (cancelled) return;
        // Newest Rejected version (in case multiple round-trips happened).
        const rejected = all
          .filter((v) => v.status === 'Rejected')
          .sort((a, b) => b.versionNumber - a.versionNumber)[0];
        setVersion(rejected ?? null);
      } catch (err) {
        if (!cancelled)
          message.error(extractApiError(err).message ?? t('workflow.versions.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workflow, t]);

  const displayName =
    (i18n.language.startsWith('fr') ? workflow?.nameFr : workflow?.nameEn) ||
    workflow?.nameEn ||
    workflow?.appName ||
    '';

  return (
    <BmoModal
      open={workflow !== null}
      onCancel={onClose}
      width={520}
      footer={null}
      title={t('workflow.list.rejectionDetailsTitle')}
      bodyStyle={{ padding: 0, background: '#ffffff' }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !version ? (
        <div style={{ padding: 24 }}>
          <Alert type="info" message={t('workflow.list.rejectionNotFound')} />
        </div>
      ) : (
        <div style={{ padding: '24px 28px' }}>
          {/* Workflow name — heading for the whole surface. */}
          <Text
            strong
            style={{
              display: 'block',
              fontSize: 22,
              lineHeight: 1.3,
              color: '#0f172a',
              marginBottom: 6,
            }}
          >
            {displayName}
          </Text>

          {/* Encouraging sub-line so the modal doesn't feel purely
              negative — "fix and resubmit" primes the next action. */}
          <Text style={{ fontSize: 14, color: '#475569', display: 'block', marginBottom: 22 }}>
            {t('workflow.list.rejectionHint')}
          </Text>

          {/* THE comment — the star of the modal. Large quote-style
              block with a soft-red rail down the left and a big pull-
              quote glyph in the top-left so it reads as a spoken
              message from the approver, not a form field. */}
          <div
            style={{
              position: 'relative',
              background: '#fefafa',
              border: '1px solid #fee2e2',
              borderRadius: 12,
              padding: '22px 24px 22px 30px',
              boxShadow: '0 1px 2px rgba(220, 38, 38, 0.04)',
            }}
          >
            {/* Left accent rail — 4 px BMO-red so the card reads as
                "rejection copy" even without reading the eyebrow. */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                left: 0,
                top: 12,
                bottom: 12,
                width: 4,
                background: '#dc2626',
                borderRadius: 4,
              }}
            />
            {/* Big pull-quote glyph — using Unicode LEFT DOUBLE
                QUOTATION MARK, not an icon font, so it renders the
                same across locales without the AntD icon size quirks. */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                left: 18,
                top: -6,
                fontSize: 44,
                lineHeight: 1,
                color: '#fecaca',
                fontFamily: 'Georgia, "Times New Roman", serif',
                userSelect: 'none',
              }}
            >
              &ldquo;
            </div>
            <Paragraph
              style={{
                marginBottom: 0,
                whiteSpace: 'pre-wrap',
                fontSize: 15.5,
                color: '#111827',
                lineHeight: 1.6,
                fontStyle: version.rejectionComment ? 'normal' : 'italic',
              }}
            >
              {version.rejectionComment || t('workflow.details.notProvided')}
            </Paragraph>
          </div>

          {/* Signature footer — muted line naming the approver and
              when. Reads as "— Alice · Jul 8, 2026, 3:15 PM" like an
              email signature so the comment above feels attributed
              without a heavy metadata table. */}
          {(version.approvedBy || version.approvedAt) && (
            <div
              style={{
                marginTop: 16,
                textAlign: 'right',
                fontSize: 13,
                color: '#64748b',
              }}
            >
              —{' '}
              {version.approvedBy && <Text style={{ color: '#334155' }}>{version.approvedBy}</Text>}
              {version.approvedBy && version.approvedAt && <span> · </span>}
              {version.approvedAt && (
                <span>{new Date(version.approvedAt).toLocaleString(i18n.language)}</span>
              )}
            </div>
          )}

          {/* Bottom action row. Single Close button — the modal is
              read-only; the actual "fix and resubmit" flow lives on
              the card the user came from. */}
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <Button onClick={onClose}>{t('common.gotIt')}</Button>
          </div>
        </div>
      )}
    </BmoModal>
  );
}
